/**
 * assets/collection-tabs.js
 * ------------------------------------------------------------------
 * Vanilla JS controller for the "Collection Tabs" section.
 *
 * Responsibilities:
 *  - Accessible tab switching (click + keyboard) between pre-rendered
 *    panels, with no page reload and no network request.
 *  - A lightweight touch/mouse-draggable slider for panels whose
 *    layout is set to "slider" (desktop and/or mobile), including
 *    optional infinite loop, arrows, dots and autoplay.
 *  - Quick add to cart via the Ajax Cart API.
 *
 * No external dependencies. No jQuery.
 */
(function () {
  'use strict';

  var MOBILE_BREAKPOINT = 749; // matches the CSS breakpoint (max mobile width)
  var DRAG_THRESHOLD = 40; // px the pointer must move to register a swipe

  /* ------------------------------------------------------------------ */
  /* Utilities                                                          */
  /* ------------------------------------------------------------------ */

  function isDesktop() {
    return window.matchMedia('(min-width: 750px)').matches;
  }

  function debounce(fn, wait) {
    var timeout;
    return function () {
      var args = arguments;
      var context = this;
      window.clearTimeout(timeout);
      timeout = window.setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    };
  }

  /* ------------------------------------------------------------------ */
  /* Slider                                                             */
  /* ------------------------------------------------------------------ */

  /**
   * A minimal, dependency-free slider that supports touch/mouse drag,
   * optional infinite loop, arrows, dots, autoplay and pause-on-hover.
   * One instance is created per <track> element (data-products-track)
   * only while that track is in "slider" mode for the current viewport.
   */
  function CollectionTabsSlider(track, options) {
    this.track = track;
    this.panel = track.closest('[data-panel]');
    this.options = options;
    this.originalSlides = Array.prototype.slice.call(
      track.querySelectorAll(':scope > [data-slide]')
    );
    this.slideCount = this.originalSlides.length;
    this.currentIndex = 0;
    this.isDragging = false;
    this.startX = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    this.autoplayTimer = null;
    this.cloneCount = 0;

    if (this.slideCount < 2) {
      return; // nothing to slide
    }

    this._onPointerDown = this._onPointerDown.bind(this);
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerUp = this._onPointerUp.bind(this);
    this._onResize = debounce(this._setPositionInstant.bind(this), 150);

    this._build();
  }

  CollectionTabsSlider.prototype._build = function () {
    var track = this.track;

    if (this.options.loop) {
      this.cloneCount = Math.min(this.slideCount, 3);
      for (var i = 0; i < this.cloneCount; i++) {
        var lastClone = this.originalSlides[this.slideCount - 1 - i].cloneNode(true);
        var firstClone = this.originalSlides[i].cloneNode(true);
        lastClone.setAttribute('aria-hidden', 'true');
        lastClone.setAttribute('tabindex', '-1');
        firstClone.setAttribute('aria-hidden', 'true');
        firstClone.setAttribute('tabindex', '-1');
        track.insertBefore(lastClone, track.firstChild);
        track.appendChild(firstClone);
      }
      this.currentIndex = this.cloneCount;
    }

    this.slides = Array.prototype.slice.call(track.querySelectorAll(':scope > [data-slide]'));

    track.style.willChange = 'transform';
    track.classList.add('is-slider-initialized');

    this._bindEvents();
    this._buildDots();
    this._setPositionInstant();
    this._updateArrows();
    if (this.options.autoplay) {
      this._startAutoplay();
    }
  };

  CollectionTabsSlider.prototype._bindEvents = function () {
    var track = this.track;

    track.addEventListener('pointerdown', this._onPointerDown);
    track.addEventListener('pointermove', this._onPointerMove);
    track.addEventListener('pointerup', this._onPointerUp);
    track.addEventListener('pointerleave', this._onPointerUp);
    track.addEventListener('dragstart', function (e) {
      e.preventDefault();
    });

    window.addEventListener('resize', this._onResize);

    if (this.options.pauseOnHover && this.options.autoplay) {
      track.addEventListener('mouseenter', this._stopAutoplay.bind(this));
      track.addEventListener('mouseleave', this._startAutoplay.bind(this));
    }

    var controlsWrap = this.panel.querySelector('.collection-tabs__slider-controls');
    if (controlsWrap) {
      var prevBtn = controlsWrap.querySelector('[data-slider-prev]');
      var nextBtn = controlsWrap.querySelector('[data-slider-next]');
      if (prevBtn) {
        this._prevBtn = prevBtn;
        prevBtn.addEventListener('click', this.prev.bind(this));
      }
      if (nextBtn) {
        this._nextBtn = nextBtn;
        nextBtn.addEventListener('click', this.next.bind(this));
      }
    }
  };

  CollectionTabsSlider.prototype._buildDots = function () {
    var dotsWrap = this.panel.querySelector('[data-slider-dots]');
    if (!dotsWrap) return;
    this._dotsWrap = dotsWrap;
    dotsWrap.innerHTML = '';
    this._dots = [];
    for (var i = 0; i < this.slideCount; i++) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'collection-tabs__dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
      if (i === 0) dot.classList.add('is-active');
      (function (self, index) {
        dot.addEventListener('click', function () {
          self.goTo(index);
        });
      })(this, i);
      dotsWrap.appendChild(dot);
      this._dots.push(dot);
    }
  };

  CollectionTabsSlider.prototype._updateDots = function () {
    if (!this._dots) return;
    var realIndex = this._getRealIndex();
    this._dots.forEach(function (dot, i) {
      dot.classList.toggle('is-active', i === realIndex);
    });
  };

  CollectionTabsSlider.prototype._updateArrows = function () {
    if (this.options.loop) return; // arrows always enabled when looping
    if (this._prevBtn) this._prevBtn.disabled = this.currentIndex <= 0;
    if (this._nextBtn) this._nextBtn.disabled = this.currentIndex >= this.slideCount - 1;
  };

  CollectionTabsSlider.prototype._getRealIndex = function () {
    var index = this.currentIndex - this.cloneCount;
    if (index < 0) index += this.slideCount;
    return index % this.slideCount;
  };

  CollectionTabsSlider.prototype._slideWidth = function () {
    return this.slides[0].getBoundingClientRect().width + this._gap();
  };

  CollectionTabsSlider.prototype._gap = function () {
    var styles = window.getComputedStyle(this.track);
    return parseFloat(styles.columnGap || styles.gap || '0') || 0;
  };

  CollectionTabsSlider.prototype._setPositionInstant = function () {
    var offset = -this.currentIndex * this._slideWidth();
    this.track.style.transition = 'none';
    this.track.style.transform = 'translateX(' + offset + 'px)';
    this.currentTranslate = offset;
    this.prevTranslate = offset;
  };

  CollectionTabsSlider.prototype._setPositionAnimated = function () {
    var offset = -this.currentIndex * this._slideWidth();
    this.track.style.transition = 'transform 0.4s ease';
    this.track.style.transform = 'translateX(' + offset + 'px)';
    this.currentTranslate = offset;
    this.prevTranslate = offset;
  };

  CollectionTabsSlider.prototype.next = function () {
    if (!this.options.loop && this.currentIndex >= this.slides.length - 1) return;
    this.currentIndex++;
    this._setPositionAnimated();
    this._afterMove();
  };

  CollectionTabsSlider.prototype.prev = function () {
    if (!this.options.loop && this.currentIndex <= 0) return;
    this.currentIndex--;
    this._setPositionAnimated();
    this._afterMove();
  };

  CollectionTabsSlider.prototype.goTo = function (realIndex) {
    this.currentIndex = realIndex + this.cloneCount;
    this._setPositionAnimated();
    this._afterMove();
  };

  CollectionTabsSlider.prototype._afterMove = function () {
    this._updateDots();
    this._updateArrows();

    var self = this;
    if (this.options.loop) {
      window.clearTimeout(this._loopTimeout);
      this._loopTimeout = window.setTimeout(function () {
        if (self.currentIndex >= self.slides.length - self.cloneCount) {
          self.currentIndex = self.cloneCount;
          self._setPositionInstant();
        } else if (self.currentIndex < self.cloneCount) {
          self.currentIndex = self.slides.length - self.cloneCount * 2 + self.currentIndex;
          self._setPositionInstant();
        }
      }, 420);
    }
  };

  CollectionTabsSlider.prototype._onPointerDown = function (e) {
    this.isDragging = true;
    this.startX = e.clientX;
    this.track.style.transition = 'none';
    this.track.classList.add('is-dragging');
    this._stopAutoplay();
    if (this.track.setPointerCapture && e.pointerId != null) {
      try {
        this.track.setPointerCapture(e.pointerId);
      } catch (err) {
        /* ignore unsupported pointer capture */
      }
    }
  };

  CollectionTabsSlider.prototype._onPointerMove = function (e) {
    if (!this.isDragging) return;
    var delta = e.clientX - this.startX;
    this.currentTranslate = this.prevTranslate + delta;
    this.track.style.transform = 'translateX(' + this.currentTranslate + 'px)';
  };

  CollectionTabsSlider.prototype._onPointerUp = function (e) {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.track.classList.remove('is-dragging');

    var delta = this.currentTranslate - this.prevTranslate;
    if (delta < -DRAG_THRESHOLD) {
      this.next();
    } else if (delta > DRAG_THRESHOLD) {
      this.prev();
    } else {
      this._setPositionAnimated();
    }

    if (this.options.autoplay) this._startAutoplay();
  };

  CollectionTabsSlider.prototype._startAutoplay = function () {
    if (!this.options.autoplay) return;
    this._stopAutoplay();
    var self = this;
    this.autoplayTimer = window.setInterval(function () {
      self.next();
    }, this.options.autoplaySpeed * 1000);
  };

  CollectionTabsSlider.prototype._stopAutoplay = function () {
    if (this.autoplayTimer) {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  };

  CollectionTabsSlider.prototype.destroy = function () {
    this._stopAutoplay();
    window.removeEventListener('resize', this._onResize);
    this.track.style.transition = '';
    this.track.style.transform = '';
    this.track.style.willChange = '';
    this.track.classList.remove('is-slider-initialized');

    if (this.options.loop) {
      // Remove clones, restore the original slide set.
      var track = this.track;
      Array.prototype.slice.call(track.querySelectorAll(':scope > [data-slide][aria-hidden="true"]')).forEach(function (clone) {
        clone.remove();
      });
    }
  };

  /* ------------------------------------------------------------------ */
  /* Tabs controller                                                    */
  /* ------------------------------------------------------------------ */

  function CollectionTabs(root) {
    this.root = root;
    this.sectionId = root.getAttribute('data-section-id');
    this.tabs = Array.prototype.slice.call(root.querySelectorAll('[data-tab]'));
    this.panels = Array.prototype.slice.call(root.querySelectorAll('[data-panel]'));
    this.sliders = {}; // panelIndex -> CollectionTabsSlider

    this.desktopLayout = root.getAttribute('data-desktop-layout');
    this.mobileLayout = root.getAttribute('data-mobile-layout');

    this.sliderOptions = {
      loop: root.getAttribute('data-enable-loop') === 'true',
      autoplay: root.getAttribute('data-enable-autoplay') === 'true',
      autoplaySpeed: parseFloat(root.getAttribute('data-autoplay-speed')) || 4,
      pauseOnHover: true
    };

    this._bindTabs();
    this._bindQuickAdd();
    this._bindSwatches();
    this._initSlidersForViewport();

    window.addEventListener(
      'resize',
      debounce(this._initSlidersForViewport.bind(this), 200)
    );
  }

  CollectionTabs.prototype._bindTabs = function () {
    var self = this;

    this.tabs.forEach(function (tab, index) {
      tab.addEventListener('click', function () {
        self.activate(index);
      });

      tab.addEventListener('keydown', function (e) {
        var newIndex = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          newIndex = (index + 1) % self.tabs.length;
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          newIndex = (index - 1 + self.tabs.length) % self.tabs.length;
        } else if (e.key === 'Home') {
          newIndex = 0;
        } else if (e.key === 'End') {
          newIndex = self.tabs.length - 1;
        }

        if (newIndex !== null) {
          e.preventDefault();
          self.activate(newIndex);
          self.tabs[newIndex].focus();
        }
      });
    });
  };

  CollectionTabs.prototype.activate = function (index) {
    if (index === this._activeIndex) return;
    this._activeIndex = index;

    this.tabs.forEach(function (tab, i) {
      var isActive = i === index;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    this.panels.forEach(function (panel, i) {
      var isActive = i === index;
      panel.classList.toggle('is-active', isActive);
      if (isActive) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    // Lazily initialize a slider the first time its panel becomes active
    // (avoids measuring hidden elements, which would report 0 width).
    this._initSliderForPanel(index);
  };

  CollectionTabs.prototype._layoutForViewport = function () {
    return isDesktop() ? this.desktopLayout : this.mobileLayout;
  };

  CollectionTabs.prototype._initSlidersForViewport = function () {
    var layout = this._layoutForViewport();

    // Destroy sliders that no longer match the current viewport's layout.
    Object.keys(this.sliders).forEach(
      function (panelIndex) {
        if (layout !== 'slider') {
          this.sliders[panelIndex].destroy();
          delete this.sliders[panelIndex];
        }
      }.bind(this)
    );

    if (layout === 'slider') {
      var activeIndex = this._activeIndex || 0;
      this._initSliderForPanel(activeIndex);
    }
  };

  CollectionTabs.prototype._initSliderForPanel = function (panelIndex) {
    var layout = this._layoutForViewport();
    if (layout !== 'slider') return;
    if (this.sliders[panelIndex]) return; // already initialized

    var panel = this.panels[panelIndex];
    if (!panel) return;
    var track = panel.querySelector('[data-products-track]');
    if (!track) return;

    this.sliders[panelIndex] = new CollectionTabsSlider(track, this.sliderOptions);
  };

  /* ------------------------------------------------------------------ */
  /* Quick add                                                          */
  /* ------------------------------------------------------------------ */

  CollectionTabs.prototype._bindQuickAdd = function () {
    var self = this;
    var addUrl = this.root.getAttribute('data-cart-add-url');

    this.root.addEventListener('click', function (e) {
      var button = e.target.closest('[data-quick-add]');
      if (!button || !self.root.contains(button)) return;

      var variantId = button.getAttribute('data-variant-id');
      if (!variantId || button.disabled) return;

      button.disabled = true;
      var originalLabel = button.querySelector('.product-card__quick-add-label').textContent;

      fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) throw new Error(data.description || 'Unable to add to cart');
            return data;
          });
        })
        .then(function () {
          button.classList.add('is-added');
          button.querySelector('.product-card__quick-add-label').textContent = 'Added';
          document.dispatchEvent(
            new CustomEvent('collection-tabs:product-added', {
              bubbles: true,
              detail: { variantId: variantId }
            })
          );
          window.setTimeout(function () {
            button.classList.remove('is-added');
            button.querySelector('.product-card__quick-add-label').textContent = originalLabel;
            button.disabled = false;
          }, 1800);
        })
        .catch(function () {
          button.querySelector('.product-card__quick-add-label').textContent = 'Error';
          window.setTimeout(function () {
            button.querySelector('.product-card__quick-add-label').textContent = originalLabel;
            button.disabled = false;
          }, 1800);
        });
    });
  };

  /* ------------------------------------------------------------------ */
  /* Color swatches (visual variant preview)                            */
  /* ------------------------------------------------------------------ */

  CollectionTabs.prototype._bindSwatches = function () {
    this.root.addEventListener('click', function (e) {
      var swatch = e.target.closest('[data-swatch]');
      if (!swatch) return;

      var card = swatch.closest('[data-product-card]');
      if (!card) return;

      var image = card.querySelector('[data-product-image]');
      var newSrc = swatch.getAttribute('data-swatch-image');
      if (image && newSrc) {
        image.src = newSrc;
      }

      var group = swatch.parentElement;
      Array.prototype.slice.call(group.children).forEach(function (el) {
        el.classList.remove('is-selected');
      });
      swatch.classList.add('is-selected');
    });
  };

  /* ------------------------------------------------------------------ */
  /* Init                                                               */
  /* ------------------------------------------------------------------ */

  function initAll() {
    var sections = document.querySelectorAll('[data-collection-tabs]');
    sections.forEach(function (section) {
      if (section.collectionTabsInstance) return;
      section.collectionTabsInstance = new CollectionTabs(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor support: re-init when a section is added/reloaded.
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector('[data-collection-tabs]');
    if (section) {
      section.collectionTabsInstance = new CollectionTabs(section);
    }
  });
})();
